import { useCallback, useEffect, useMemo, useState } from 'react';
import HostTarget from '../types/host/HostTarget';
import AgentApi from '../api/instance/AgentApi';
import Agent from '../types/instance/Agent';

interface HostFilterState {
    type: string[];
    sensorstatus: string[];
    agent: string[];
}

interface HostFilterOptions {
    types: string[];
    sensorstatuses: Array<{ label: string; value: string }>;
    agents: Array<{ label: string; value: string }>;
}

interface UseHostFiltersOptions {
    /**
     * Host data to filter
     */
    hosts: HostTarget[];
    /**
     * Initial filter state
     */
    initialFilters?: Partial<HostFilterState>;
}

interface UseHostFiltersReturn {
    // Filter state
    filters: HostFilterState;
    updateFilter: (filterName: string, values: string[]) => void;
    clearAllFilters: () => void;
    
    // Filter options
    availableFilterOptions: HostFilterOptions;
    
    // Helpers
    getSensorStatusLabel: (host: HostTarget) => string;
    getFilteredHosts: (hostsToFilter?: HostTarget[]) => HostTarget[];
    
    // Loading state
    filtersLoading: boolean;
}

const defaultFilters: HostFilterState = {
    type: [],
    sensorstatus: [],
    agent: []
};

/**
 * Custom hook for managing host filtering logic
 */
export function useHostFilters(options: UseHostFiltersOptions): UseHostFiltersReturn {
    const { 
        hosts,
        initialFilters = {}
    } = options;

    const [filters, setFilters] = useState<HostFilterState>({
        ...defaultFilters,
        ...initialFilters
    });
    
    const [filtersLoading, setFiltersLoading] = useState<boolean>(false);
    const [agents, setAgents] = useState<Agent[]>([]);

    // Fetch agents on mount
    useEffect(() => {
        const fetchAgents = async () => {
            setFiltersLoading(true);
            try {
                const fetchedAgents = await AgentApi.getAgents();
                setAgents(fetchedAgents);
            } catch (error) {
                console.error('Failed to fetch agents for filtering:', error);
            }
            setFiltersLoading(false);
        };
        fetchAgents();
    }, []);

    // Helper for Sensor Status
    const getSensorStatusLabel = useCallback((host: HostTarget): string => {
        if (host.state?.toLowerCase() !== 'started') return 'Offline';
        if (host.sensorstatus === 200) return 'Healthy';
        return 'Issue';
    }, []);

    // Helper to update filters
    const updateFilter = useCallback((filterName: string, values: string[]) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: values
        }));
    }, []);

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    // Calculate unique filter options from hosts
    const availableFilterOptions = useMemo((): HostFilterOptions => {
        const types = new Set<string>();
        const sensorstatuses = new Set<string>();
        const agentIds = new Set<number>();
        
        hosts.forEach(host => {
            if (host.type) types.add(host.type);
            sensorstatuses.add(getSensorStatusLabel(host));
            if (host.agentid) agentIds.add(host.agentid);
        });

        // Map agent IDs to names
        const agentOptions = Array.from(agentIds)
            .map(agentId => {
                const agent = agents.find(a => a.id === agentId);
                return {
                    label: agent?.name || `Agent ${agentId}`,
                    value: agentId.toString()
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        return {
            types: Array.from(types).sort(),
            sensorstatuses: Array.from(sensorstatuses).sort().map(status => ({ label: status, value: status })),
            agents: agentOptions
        };
    }, [hosts, agents, getSensorStatusLabel]);

    // Helper function to filter hosts based on current filter state
    const getFilteredHosts = useCallback((hostsToFilter?: HostTarget[]): HostTarget[] => {
        const targetHosts = hostsToFilter || hosts;
        let filtered = targetHosts;

        // Filter by Sensor Status
        if (filters.sensorstatus && filters.sensorstatus.length > 0) {
            filtered = filtered.filter(host =>
                filters.sensorstatus.includes(getSensorStatusLabel(host))
            );
        }

        // Filter by Type
        if (filters.type.length > 0) {
            filtered = filtered.filter(host =>
                host.type && filters.type.includes(host.type)
            );
        }

        // Filter by Agent
        if (filters.agent.length > 0) {
            filtered = filtered.filter(host =>
                host.agentid && filters.agent.includes(host.agentid.toString())
            );
        }

        return filtered;
    }, [filters, hosts, getSensorStatusLabel]);

    return {
        filters,
        updateFilter,
        clearAllFilters,
        availableFilterOptions,
        getSensorStatusLabel,
        getFilteredHosts,
        filtersLoading
    };
}
